import React, { useState, useEffect } from 'react';
import { Form, Input, Button, DatePicker, Select, InputNumber, Row, Col, Typography, Card, message, Spin, Grid, Modal } from 'antd';
import { PlusOutlined, DeleteOutlined, ArrowLeftOutlined, SearchOutlined, EyeOutlined } from '@ant-design/icons';
import { useNavigate, useLocation } from 'react-router-dom';
import { userService, folioSerieService, companiesService, certificatesService, receiversService, companyTaxService, productsService, taxTemplateService } from '../../services/api';
import CompanyInfoSection from './components/CompanyInfoSection';
import ClientInfoSection from './components/ClientInfoSection';
import ItemsTableSection from './components/ItemsTableSection';
import ConditionsSection from './components/ConditionsSection';
import ActionsSection from './components/ActionsSection';

const { Title, Text } = Typography;
const { Option } = Select;
const { useBreakpoint } = Grid;

const CreateInvoice = () => {
  const screens = useBreakpoint();
  const navigate = useNavigate();
  const location = useLocation();
  const [form] = Form.useForm();
  const [clients, setClients] = useState([]);
  const [products, setProducts] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [folioSeries, setFolioSeries] = useState([]);
  const [certificates, setCertificates] = useState([]);
  const [invoiceItems, setInvoiceItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [draftLoading, setDraftLoading] = useState(false);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [pdfPreviewUrl, setPdfPreviewUrl] = useState(null);
  const [previewVisible, setPreviewVisible] = useState(false);
  const [subtotal, setSubtotal] = useState(0);
  const [taxes, setTaxes] = useState([]); // Impuestos calculados dinámicamente
  const [total, setTotal] = useState(0);
  const [selectedClient, setSelectedClient] = useState(null);
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [activeTaxes, setActiveTaxes] = useState([]);
  const [taxTemplates, setTaxTemplates] = useState([]);
  const [loadedInvoice, setLoadedInvoice] = useState(null);
  const [existingInvoiceDate, setExistingInvoiceDate] = useState(null); // Impuestos activos de la empresa
  const [initialDate] = useState(new Date()); // Fecha inicial para el valor por defecto
  const [selectedCertificateId, setSelectedCertificateId] = useState(null);
  const [certificateAlert, setCertificateAlert] = useState(null);
  const searchTimeoutRef = React.useRef(null);

  // Detectar si viene un documento precargado o para clonar
  const initialInvoiceId = location.state?.invoiceId;
  const cloneFromId = location.state?.cloneFromId;

  // Opciones de Uso CFDI según el catálogo del SAT
  const usoCfdiOptions = [
    { value: 'G01', label: 'G01 - Adquisición de mercancías' },
    { value: 'G02', label: 'G02 - Devoluciones, descuentos o bonificaciones' },
    { value: 'G03', label: 'G03 - Gastos en general' },
    { value: 'I01', label: 'I01 - Construcciones' },
    { value: 'I02', label: 'I02 - Mobilario y equipo de oficina por inversiones' },
    { value: 'I03', label: 'I03 - Equipo de transporte' },
    { value: 'I04', label: 'I04 - Equipo de computo y accesorios' },
    { value: 'I05', label: 'I05 - Dados, troqueles, moldes, matrices y herramental' },
    { value: 'I06', label: 'I06 - Comunicaciones telefónicas' },
    { value: 'I07', label: 'I07 - Comunicaciones satelitales' },
    { value: 'I08', label: 'I08 - Otra maquinaria y equipo' },
    { value: 'D01', label: 'D01 - Honorarios médicos, dentales y gastos hospitalarios' },
    { value: 'D02', label: 'D02 - Gastos médicos por incapacidad o discapacidad' },
    { value: 'D03', label: 'D03 - Gastos funerales' },
    { value: 'D04', label: 'D04 - Donativos' },
    { value: 'D05', label: 'D05 - Intereses reales efectivamente pagados por créditos hipotecarios' },
    { value: 'D06', label: 'D06 - Aportaciones voluntarias al SAR' },
    { value: 'D07', label: 'D07 - Primas por seguros de gastos médicos' },
    { value: 'D08', label: 'D08 - Gastos de transportación escolar obligatoria' },
    { value: 'D09', label: 'D09 - Depósitos en cuentas para el ahorro, primas que tengan como base planes de pensiones' },
    { value: 'D10', label: 'D10 - Pagos por servicios educativos (colegiaturas)' },
    { value: 'P01', label: 'P01 - Por definir' },
    { value: 'S01', label: 'S01 - Sin efectos fiscales' },
    { value: 'CP01', label: 'CP01 - Pagos' },
    { value: 'CN01', label: 'CN01 - Nómina' }
  ];

  const metodoPagoOptions = [
    { value: 'PUE', label: 'PUE - Pago en una sola exhibición' },
    { value: 'PPD', label: 'PPD - Pago en parcialidades o diferido' }
  ];
  const tipoComprobanteOptions = [
    { value: 'I', label: 'I - Ingreso' },
    { value: 'E', label: 'E - Egreso' },
    { value: 'T', label: 'T - Traslado' },
    { value: 'N', label: 'N - Nómina' },
    { value: 'P', label: 'P - Pago' }
  ];

  // Opciones de Forma de Pago según el catálogo del SAT
  const formaPagoOptions = [
    { value: '01', label: '01 - Efectivo' },
    { value: '02', label: '02 - Cheque nominativo' },
    { value: '03', label: '03 - Transferencia electrónica de fondos' },
    { value: '04', label: '04 - Tarjeta de crédito' },
    { value: '05', label: '05 - Monedero electrónico' },
    { value: '06', label: '06 - Dinero electrónico' },
    { value: '08', label: '08 - Vales de despensa' },
    { value: '12', label: '12 - Dación en pago' },
    { value: '13', label: '13 - Pago por subrogación' },
    { value: '14', label: '14 - Pago por consignación' },
    { value: '15', label: '15 - Condonación' },
    { value: '17', label: '17 - Compensación' },
    { value: '23', label: '23 - Novación' },
    { value: '24', label: '24 - Confusión' },
    { value: '25', label: '25 - Remisión de deuda' },
    { value: '26', label: '26 - Prescripción o caducidad' },
    { value: '27', label: '27 - A satisfacción del acreedor' },
    { value: '28', label: '28 - Tarjeta de débito' },
    { value: '29', label: '29 - Tarjeta de servicios' },
    { value: '30', label: '30 - Aplicación de anticipos' },
    { value: '31', label: '31 - Intermediario pago' },
    { value: '99', label: '99 - Por definir' }
  ];

  const parseMysqlDateTime = (value) => {
    if (!value) return null;
    const raw = String(value).trim();
    if (!raw) return null;
    if (raw.includes('T')) {
      const d = new Date(raw);
      return Number.isNaN(d.getTime()) ? null : d;
    }
    const parts = raw.split(/[- :]/).filter(Boolean);
    if (parts.length < 3) return null;
    const y = Number(parts[0]);
    const m = Number(parts[1]);
    const d = Number(parts[2]);
    const hh = parts.length >= 4 ? Number(parts[3]) : 0;
    const mm = parts.length >= 5 ? Number(parts[4]) : 0;
    const ss = parts.length >= 6 ? Number(parts[5]) : 0;
    if (!y || !m || !d) return null;
    const date = new Date(y, m - 1, d, hh, mm, ss);
    return Number.isNaN(date.getTime()) ? null : date;
  };

  const evaluateSelectedCertificate = (certId, certList) => {
    if (!certId) return null;
    const list = Array.isArray(certList) ? certList : [];
    const selected = list.find((c) => String(c?.id) === String(certId));
    if (!selected) {
      return { type: 'error', message: 'El certificado seleccionado no existe. Selecciona un CSD válido.' };
    }
    const exp = parseMysqlDateTime(selected?.expiration_date);
    if (!exp) {
      return { type: 'error', message: 'No se pudo validar la vigencia del CSD. Sube un certificado vigente.' };
    }
    const now = new Date();
    const daysLeft = Math.ceil((exp.getTime() - now.getTime()) / (24 * 60 * 60 * 1000));
    const dateText = exp.toLocaleDateString('es-MX');
    if (exp.getTime() < now.getTime()) {
      return { type: 'error', message: `El CSD seleccionado está vencido (${dateText}). Sube uno vigente para poder timbrar.` };
    }
    if (daysLeft <= 30) {
      return { type: 'warning', message: `Tu CSD está por vencer (${dateText}). Considera subir uno vigente para evitar errores al timbrar.` };
    }
    return null;
  };

  useEffect(() => {
    // Cargar catálogos base
    fetchClients(); // Cargar iniciales (o vacíos si es mucho)
    fetchProducts();
    // Evitar setear empresa por defecto si hay documento precargado o para clonar
    fetchCompanies({ setDefault: !initialInvoiceId && !cloneFromId });

    // Precargar borrador o factura a clonar
    const idToLoad = initialInvoiceId || cloneFromId;
    if (idToLoad) {
      loadInvoice(idToLoad);
    }
  }, []);

  const handleClientSearch = (value) => {
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    searchTimeoutRef.current = setTimeout(() => {
      fetchClients(value);
    }, 500);
  };

  useEffect(() => {
    // Recalcular impuestos por ítem cuando cambian los impuestos activos
    setInvoiceItems(prevItems =>
      prevItems.map(item => {
        const taxes = computeItemTaxes(item);
        const hasTaxes = (taxes.Traslados.length > 0 || taxes.Retenciones.length > 0);
        return { ...item, taxes, objeto_imp: hasTaxes ? '02' : '01' };
      })
    );
  }, [activeTaxes]);

  useEffect(() => {
    calculateTotals();
  }, [invoiceItems, activeTaxes]); // Agregar activeTaxes como dependencia

  const fetchClients = async (search = '') => {
    try {
      const response = await receiversService.getReceivers(search);
      const list = response.data || [];
      
      // Si tenemos un cliente seleccionado y no está en la lista de búsqueda, lo mantenemos
      if (selectedClient && !list.find(c => c.id === selectedClient.id)) {
          list.push(selectedClient);
      }
      
      setClients(list);
    } catch (error) {
      console.error('Error fetching clients:', error);
    }
  };

  const fetchProducts = async (search = '') => {
    try {
      const response = await productsService.getProducts(search);
      const list = Array.isArray(response) ? response : (response.data || []);
      setProducts(list);
    } catch (error) {
      console.error('Error fetching products:', error);
      setProducts([]);
    }
  };

  const fetchCompanies = async (options = {}) => {
    const { setDefault = true } = options;
    try {
      const response = await companiesService.getCompanies();
      setCompanies(response.data.companies || []);

      // Solo seleccionar por defecto si está permitido y no estamos precargando
      
      if (setDefault && response.data.companies && response.data.companies.length > 0) {
        const firstCompany = response.data.companies[0];
        setSelectedCompany(firstCompany);
        fetchFolioSeries(firstCompany.id);
        fetchCertificates(firstCompany.id);
        fetchActiveTaxes(firstCompany.id); // Agregar esta línea
        fetchTaxTemplates(firstCompany.id);
        form.setFieldsValue({ company_id: firstCompany.id });
      }
    } catch (error) {
      console.error('Error fetching companies:', error);
    }
  };

  const fetchTaxTemplates = async (companyId) => {
    try {
      const response = await taxTemplateService.getTemplates(companyId);
      setTaxTemplates(response.data.templates || []);
    } catch (error) {
      console.error('Error fetching tax templates:', error);
      setTaxTemplates([]);
    }
  };

  const fetchActiveTaxes = async (companyId) => {
    try {
      
      const response = await companyTaxService.getTaxes(companyId, { is_active: true });
      const taxes = response.data.taxes || [];
      setActiveTaxes(taxes);
      return taxes;
    } catch (error) {
      console.error('Error fetching active taxes:', error);
      setActiveTaxes([]);
      return [];
    }
  };

  const fetchFolioSeries = async (companyId) => {
    try {
      const response = await folioSerieService.getFolioSeries(companyId, { enabled: true });
      setFolioSeries(response.data || []);
    } catch (error) {
      console.error('Error fetching folio series:', error);
    }
  };

  const fetchCertificates = async (companyId) => {
    try {
      const response = await certificatesService.getCertificates(companyId);
      const list = response.data || [];
      setCertificates(list);
      setCertificateAlert(evaluateSelectedCertificate(selectedCertificateId, list));
    } catch (error) {
      console.error('Error fetching certificates:', error);
      setCertificates([]);
      setCertificateAlert(null);
    }
  };

  const handleCompanySelect = (companyId) => {
    const company = companies.find(c => c.id === companyId);
    if (company) {
      setSelectedCompany(company);
      fetchFolioSeries(companyId);
      fetchCertificates(companyId);
      fetchActiveTaxes(companyId); // Agregar esta línea
      fetchTaxTemplates(companyId);
      // No limpiar la serie ni certificado si hay borrador cargado
      if (!loadedInvoice) {
        form.setFieldsValue({ 
          folio_serie_id: undefined,
          certificate_id: undefined 
        });
        setSelectedCertificateId(null);
        setCertificateAlert(null);
      }
    }
  };

  const handleCertificateSelect = (certificateId) => {
    const nextId = certificateId || null;
    setSelectedCertificateId(nextId);
    setCertificateAlert(evaluateSelectedCertificate(nextId, certificates));
  };

  const handleTaxTemplateChange = (templateId) => {
    const template = taxTemplates.find(t => t.id === templateId);
    if (template && template.details) {
      const templateTaxes = template.details
        .map(d => d.company_tax)
        .filter(tax => tax);
      setActiveTaxes(templateTaxes);
      message.success(`Template "${template.name}" aplicado`);
    } else {
        if (selectedCompany) {
            fetchActiveTaxes(selectedCompany.id);
            message.info('Impuestos por defecto (activos) aplicados');
        }
    }
  };

  const roundMoney = (amount) => {
    return Math.round((amount + Number.EPSILON) * 100) / 100;
  };

  const calculateTotals = () => {
    const newSubtotal = invoiceItems.reduce((sum, item) => {
      return sum + roundMoney((item.quantity || 0) * (item.price || 0));
    }, 0);
    
    // Calcular impuestos dinámicamente iterando sobre los items y sumando los importes redondeados
    const calculatedTaxes = activeTaxes.map(tax => {
      let totalTaxAmount = 0;
      const type = tax.tax_type?.toLowerCase();
      
      if (type === 'traslado' || type === 'retencion') {
        invoiceItems.forEach(item => {
           // Si el ítem tiene impuestos seleccionados explícitamente, verificar si este impuesto está incluido
           if (item.selectedTaxIds && !item.selectedTaxIds.includes(tax.id)) {
             return;
           }

           const base = (item.quantity || 0) * (Number(item.price) || 0);
           let itemTaxAmount = 0;
           
           if (tax.tipo_factor?.toLowerCase() === 'tasa') {
             itemTaxAmount = base * (parseFloat(tax.tasa_o_cuota || 0));
           } else if (tax.tipo_factor?.toLowerCase() === 'cuota') {
             itemTaxAmount = (item.quantity || 0) * parseFloat(tax.tasa_o_cuota || 0);
           }
           
           totalTaxAmount += roundMoney(itemTaxAmount);
        });
      }
      
      return {
        ...tax,
        amount: totalTaxAmount
      };
    });
    
    const totalTaxes = calculatedTaxes.reduce((sum, tax) => {
      const type = tax.tax_type?.toLowerCase();
      if (type === 'traslado') return sum + tax.amount;
      if (type === 'retencion') return sum - tax.amount;
      return sum;
    }, 0);
    
    const newTotal = roundMoney(newSubtotal + totalTaxes);
    
    setSubtotal(newSubtotal);
    setTaxes(calculatedTaxes);
    setTotal(newTotal);
  };

  const addItem = () => {
    const newItem = {
      key: Date.now(),
      product_id: null,
      product: null,
      clave: '',
      description: '',
      quantity: 1,
      price: 0,
      amount: 0,
      selectedTaxIds: activeTaxes.map(t => t.id), // Por defecto todos los impuestos activos
      taxes: { Traslados: [], Retenciones: [] }
    };
    
    // Calcular estado inicial de impuestos
    newItem.taxes = computeItemTaxes(newItem);
    const hasTaxes = (newItem.taxes.Traslados.length > 0 || newItem.taxes.Retenciones.length > 0);
    newItem.objeto_imp = hasTaxes ? '02' : '01';

    setInvoiceItems(prev => [...prev, newItem]);
  };

  const removeItem = (key) => {
    setInvoiceItems(invoiceItems.filter(item => item.key !== key));
  };

  const computeItemTaxes = (item) => {
    const base = (item.quantity || 0) * (Number(item.price) || 0);
    const result = { Traslados: [], Retenciones: [] };

    activeTaxes.forEach(tax => {
      if (item.selectedTaxIds && !item.selectedTaxIds.includes(tax.id)) {
        return;
      }

      const tipo = (tax.tax_type || '').toLowerCase();
      const factor = tax.tipo_factor;
      const tasa = tax.tasa_o_cuota !== null && tax.tasa_o_cuota !== undefined
        ? parseFloat(tax.tasa_o_cuota)
        : null;

      let importe = 0;
      if (factor === 'Tasa' && tasa !== null) {
        importe = base * tasa;
      } else if (factor === 'Cuota' && tasa !== null) {
        const qty = item.quantity || 0;
        importe = qty * tasa;
      } else if (factor === 'Exento') {
        importe = 0;
      }

      const taxItem = {
        Base: base,
        Impuesto: tax.tax_code,       // 001 ISR, 002 IVA, 003 IEPS
        TipoFactor: factor,           // Tasa | Cuota | Exento
        TasaOCuota: tasa,             // null para Exento
        Importe: roundMoney(importe)
      };

      if (tipo === 'traslado') {
        result.Traslados.push(taxItem);
      } else if (tipo === 'retencion') {
        result.Retenciones.push(taxItem);
      }
    });

    return result;
  };

  const updateItem = (key, field, value) => {
    setInvoiceItems(prevItems =>
      prevItems.map(item => {
        if (item.key === key) {
          const updatedItem = { ...item, [field]: value };
          if (field === 'quantity' || field === 'price' || field === 'selectedTaxIds') {
            updatedItem.amount = (updatedItem.quantity || 0) * (updatedItem.price || 0);
            updatedItem.taxes = computeItemTaxes(updatedItem);
            const hasTaxes = (updatedItem.taxes.Traslados.length > 0 || updatedItem.taxes.Retenciones.length > 0);
            updatedItem.objeto_imp = hasTaxes ? '02' : '01';
          }
          return updatedItem;
        }
        return item;
      })
    );
  };

  const applyProductToRow = (key, product) => {
    if (!product) return;
    setInvoiceItems(prevItems =>
      prevItems.map(item => {
        if (item.key === key) {
          const price = Number(product.price) || 0;
          const quantity = item.quantity || 1;
          // Determine if taxes should be applied based on product flag
          // Default to true if flag is missing
          const shouldApplyTaxes = product.apply_taxes !== undefined ? Boolean(product.apply_taxes) : true;
          
          const nextItem = {
            ...item,
            product_id: product.id || null,
            product: product || null,
            clave: product.claveprod || '',
            description: product.description || product.name || '',
            price: price,
            amount: quantity * price,
            selectedTaxIds: shouldApplyTaxes ? activeTaxes.map(t => t.id) : []
          };
          nextItem.taxes = computeItemTaxes(nextItem);
          const hasTaxes = (nextItem.taxes.Traslados.length > 0 || nextItem.taxes.Retenciones.length > 0);
          nextItem.objeto_imp = hasTaxes ? '02' : '01';
          return nextItem;
        }
        return item;
      })
    );
  };

  const handleProductSearch = async (rowKey, query) => {
    const term = (query || '').trim();
    if (term.length < 2) {
      await fetchProducts('');
      return;
    }
    try {
      const resp = await productsService.searchProducts(term, 20);
      const list = resp?.data || resp || [];
      setProducts(list);
      if (list.length === 1) {
        applyProductToRow(rowKey, list[0]);
      }
    } catch (error) {
      console.error('Error searching products:', error);
    }
  };

  const handleProductSelect = async (key, productId) => {
    const product = products.find(p => p.id === productId);
    if (product) {
      applyProductToRow(key, product);
      return;
    }
    try {
      const prod = await productsService.getProduct(productId);
      applyProductToRow(key, prod);
    } catch (error) {
      console.error('Error loading selected product:', error);
    }
  };

  const handleClientSelect = (clientId) => {
    // Buscar en la lista actual o usar el seleccionado previo si coincide
    let client = clients.find(c => c.id === clientId);
    
    // Si no está en la lista actual pero es el mismo que ya estaba seleccionado, mantenerlo
    if (!client && selectedClient && selectedClient.id === clientId) {
        client = selectedClient;
    }

    if (client) {
      setSelectedClient(client);
      console.log('Selected Client:', client);
      // Rellenar automáticamente los campos del cliente
      form.setFieldsValue({
        codigo_postal: client.tax_location || '',
        regimen_fiscal: client.fiscal_regimen || 'Régimen General de Ley Personas Morales',
        uso_cfdi: client.use_tax || 'G03' // Valor por defecto G03 - Gastos en general
      });
    }
  };

  const getMexicoCityDateTimeISO = (dateInput = new Date()) => {
    const parts = new Intl.DateTimeFormat('en-CA', {
      timeZone: 'America/Mexico_City',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false,
    }).formatToParts(dateInput);
    const get = (t) => parts.find(p => p.type === t)?.value;
    return `${get('year')}-${get('month')}-${get('day')}T${get('hour')}:${get('minute')}:${get('second')}`;
  };

  const getDateOptions = () => {
    const now = new Date(); // Fecha y hora actual
    const options = [];

    // Si hay una fecha original del borrador, incluirla al inicio
    if (existingInvoiceDate) {
      const original = new Date(existingInvoiceDate);
      options.push({
        value: getMexicoCityDateTimeISO(original),
        label: `Fecha del borrador - ${original.toLocaleDateString()} ${original.toLocaleTimeString()}`
      });
    }
    
    // Fecha inicial (Default)
    if (!existingInvoiceDate) {
      options.push({
        value: getMexicoCityDateTimeISO(initialDate),
        label: `Hoy - ${initialDate.toLocaleDateString()} ${initialDate.toLocaleTimeString()}`
      });
    }

    // Hora actual (para actualizar)
    options.push({
      value: getMexicoCityDateTimeISO(now),
      label: `Ahora - ${now.toLocaleDateString()} ${now.toLocaleTimeString()}`
    });
    
    // 24 horas atrás (Hora exacta actual - 24h)
    const yesterday = new Date(now.getTime() - 24 * 60 * 60 * 1000);
    options.push({
      value: getMexicoCityDateTimeISO(yesterday),
      label: `24h - ${yesterday.toLocaleDateString()} ${yesterday.toLocaleTimeString()}`
    });
    
    // 48 horas atrás (Hora exacta actual - 48h)
    const dayAfterYesterday = new Date(now.getTime() - 48 * 60 * 60 * 1000);
    options.push({
      value: getMexicoCityDateTimeISO(dayAfterYesterday),
      label: `48h - ${dayAfterYesterday.toLocaleDateString()} ${dayAfterYesterday.toLocaleTimeString()}`
    });
    
    // 3 días atrás con margen de 1 hora para evitar quedar justo al límite al timbrar
    const threeDaysAgo = new Date(now.getTime() - 71 * 60 * 60 * 1000);
    options.push({
      value: getMexicoCityDateTimeISO(threeDaysAgo),
      label: `3 días - margen 1h - ${threeDaysAgo.toLocaleDateString()} ${threeDaysAgo.toLocaleTimeString()}`
    });

    return options;
  };

  const handleSubmit = async (values) => {
    const onFinish = async (values) => {
      if (invoiceItems.length === 0) {
        message.error('Debe agregar al menos un producto a la factura');
        return;
      }

      const certAlert = evaluateSelectedCertificate(values?.certificate_id, certificates);
      if (certAlert?.type === 'error') {
        message.error(certAlert.message);
        return;
      }

      setLoading(true);
      try {
        // Resolver folio de la serie a partir del id (si existe)
        const selectedSerie = folioSeries.find(fs => fs.id === values.folio_serie_id);

        // Normalizar ítems para garantizar clave/description/price y agregar product_id/product
        const normalizedItems = invoiceItems.map(item => {
          const product = item.product || null;
          const clave = item.clave || product?.claveprod || '';
          const description = item.description || product?.description || product?.name || '';
          const price = Number(item.price || product?.price || 0);
          const amount = roundMoney((item.quantity || 0) * price);

          return {
            ...item,
            product_id: item.product_id || product?.id || null,
            product,
            clave,
            description,
            price,
            amount,
            clave_unidad: product?.claveunidad || '',
            clave_prod_serv: product?.claveprod || '',
            cantidad: item.quantity || 0,
            descripcion: description,
            valor_unitario: price,
            importe: amount,
            impuestos: item.taxes || []
          };
        });

        const invoiceData = {
          ...values,
          lugar_expedicion: selectedCompany?.tax_location || '',
          serie: selectedSerie?.serie || values.serie,
          conceptos: normalizedItems,
          receiver_id: selectedClient?.id || null,
          subtotal,
          taxes: taxes || [],
          total
        };

        let invoiceId = loadedInvoice?.id;

        if (invoiceId) {
          // Si ya existe (borrador cargado o creado en intento anterior), actualizamos
          await userService.updateInvoice(invoiceId, invoiceData);
        } else {
          // Si no existe, creamos
          const created = await userService.createInvoice(invoiceData);
          invoiceId = created?.data?.id || created?.id;
          
          // Guardamos en estado para que si falla el timbrado, el próximo intento sea update
          if (created?.data) {
             setLoadedInvoice(created.data);
          }
        }

        if (invoiceId) {
          await userService.stampInvoice(invoiceId);
          message.success('Factura timbrada exitosamente');
          navigate('/invoices');
        } else {
          message.warning('Error: No se obtuvo ID de la factura');
        }
      } catch (error) {
        console.log('Error creating/stamping invoice:', error);
        let msg = error.response?.data?.error || 'Error al crear/timbrar la factura';
        // Si el mensaje es del tipo "Error HTTP: {...} - {...}", extraemos el JSON y el mensaje interno
        if (typeof msg === 'string' && msg.startsWith('Error HTTP:')) {
          try {
            // Extraer la parte del JSON antes del guión
            const jsonPart = msg.substring('Error HTTP:'.length, msg.indexOf(' - {')).trim();
            const errorObj = JSON.parse(jsonPart);
            if (errorObj && errorObj.message) {
              msg = errorObj.message;
            }
          } catch (e) {
            console.error('Error parsing error message:', e);
          }
        }

        message.error(msg);
      } finally {
        setLoading(false);
      }
    };

    await onFinish(values);
  };

  const loadInvoice = async (invoiceId) => {
    setLoading(true);
    try {
      const invoice = await userService.getInvoice(invoiceId);

      // Guardar referencia del borrador cargado (solo si no es clonación)
      if (!cloneFromId) {
        setLoadedInvoice(invoice.data);
      } else {
        setLoadedInvoice(null);
      }

      // Prellenar compañía y cliente
      setSelectedCompany(invoice.data.company || null);
      const receiver = invoice.data.receiver || null;
      setSelectedClient(receiver);
      
      // Asegurar que el cliente cargado esté en la lista de opciones
      if (receiver) {
        setClients(prev => {
           if (!prev.find(c => c.id === receiver.id)) {
               return [receiver, ...prev];
           }
           return prev;
        });
      }

      // Asegurar que folios, certificados e impuestos estén disponibles
      let currentActiveTaxes = [];
      if (invoice.data.company_id) {
        const results = await Promise.all([
          fetchFolioSeries(invoice.data.company_id),
          fetchCertificates(invoice.data.company_id),
          fetchActiveTaxes(invoice.data.company_id) // Asegurar impuestos activos correctos
        ]);
        currentActiveTaxes = results[2] || [];
      }

      // Normalizar fecha y opciones
      const normalizedDate = invoice.data.fecha ? getMexicoCityDateTimeISO(new Date(invoice.data.fecha)) : null;
      if (!cloneFromId) {
        setExistingInvoiceDate(normalizedDate);
      } else {
        setExistingInvoiceDate(null);
      }

      // Prellenar el formulario con la estructura homogénea
      form.setFieldsValue({
        company_id: invoice.data.company_id || undefined,
        client_id: invoice.data.receiver_id || undefined,
        folio_serie_id: cloneFromId ? undefined : (invoice.data.folio_serie_id || undefined),
        serie: cloneFromId ? undefined : (invoice.data.serie || undefined),
        fecha: cloneFromId ? getMexicoCityDateTimeISO(new Date()) : (normalizedDate || undefined),
        forma_pago: invoice.data.forma_pago || undefined,
        metodo_pago: invoice.data.metodo_pago || undefined,
        condiciones_pago: cloneFromId ? undefined : (invoice.data.condiciones_pago || undefined),
        lugar_expedicion: invoice.data.lugar_expedicion || (invoice.data.company?.tax_location || undefined),
        uso_cfdi: invoice.data.uso_cfdi || (invoice.data.receiver?.use_tax || undefined),
        codigo_postal: invoice.data.receiver?.tax_location || invoice.data.company?.tax_location || undefined,
        regimen_fiscal: invoice.data.receiver?.fiscal_regimen || invoice.data.company?.fiscal_regimen || undefined,
        tipo_comprobante: invoice.data.tipo_comprobante || undefined,
        moneda: invoice.data.moneda || 'MXN',
        tipo_cambio: invoice.data.tipo_cambio || undefined,
      });

      if (invoice?.data?.certificate_id) {
        setSelectedCertificateId(invoice.data.certificate_id);
        setCertificateAlert(evaluateSelectedCertificate(invoice.data.certificate_id, certificates));
      }

      // Normalizar conceptos a los campos internos del UI siempre con el mismo naming
      const items = (invoice.data.conceptos || []).map((c, idx) => {
        const price = Number(c.valor_unitario || 0);
        const quantity = Number(c.cantidad || 0);
        
        // Match existing taxes to active taxes IDs
        const itemSelectedTaxIds = [];
        const itemTaxes = [
            ...(c.impuestos_data?.Traslados || []),
            ...(c.impuestos_data?.Retenciones || [])
        ];

        if (currentActiveTaxes.length > 0) {
            itemTaxes.forEach(t => {
                 const match = currentActiveTaxes.find(at => {
                     const sameCode = at.tax_code === t.Impuesto;
                     const sameFactor = at.tipo_factor === t.TipoFactor;
                     const sameRate = Math.abs(Number(at.tasa_o_cuota) - Number(t.TasaOCuota)) < 0.0001;
                     return sameCode && sameFactor && (sameRate || t.TipoFactor === 'Exento');
                 });
                 if (match) itemSelectedTaxIds.push(match.id);
            });
        }

        return {
          key: `loaded-${idx}`,
          product_id: c.product_id || (c.product ? c.product.id : null),
          product: c.product || null,
          clave: c.clave_prod_serv || '',
          description: c.descripcion || '',
          price,
          quantity,
          amount: Number(c.importe || (price * quantity) || 0),
          objeto_imp: c.objeto_imp || '02',
          taxes: c.impuestos_data || { Traslados: [], Retenciones: [] },
          selectedTaxIds: itemSelectedTaxIds,
          // Campos con naming homogéneo en payload de envío
          clave_unidad: c.clave_unidad || '',
          clave_prod_serv: c.clave_prod_serv || '',
          descripcion: c.descripcion || '',
          valor_unitario: price,
          importe: Number(c.importe || (price * quantity) || 0),
          impuestos: c.impuestos_data || { Traslados: [], Retenciones: [] },
        };
      });

      setInvoiceItems(items);
      setSubtotal(Number(invoice.subtotal || 0));
      setTotal(Number(invoice.total || 0));
    } catch (error) {
      console.error('Error cargando borrador:', error);
      const msg = error.response?.data?.message || 'No se pudo cargar el borrador';
      message.error(msg);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveDraft = async () => {
    try {
      const values = await form.validateFields();
      if (invoiceItems.length === 0) {
        message.error('Debe agregar al menos un producto a la factura');
        return;
      }

      setDraftLoading(true);

      const selectedSerie = folioSeries.find(fs => fs.id === values.folio_serie_id);

      const normalizedItems = invoiceItems.map(item => {
        const product = item.product || null;
        const clave = item.clave || product?.claveprod || '';
        const description = item.description || product?.description || product?.name || '';
        const price = Number(item.price || product?.price || 0);
        const amount = (item.quantity || 0) * price;

        return {
          ...item,
          product_id: item.product_id || product?.id || null,
          product,
          clave,
          description,
          price,
          amount,
          clave_unidad: product?.claveunidad || '',
          clave_prod_serv: product?.claveprod || '',
          cantidad: item.quantity || 0,
          descripcion: description,
          valor_unitario: price,
          importe: amount,
          impuestos: item.taxes || []
        };
      });

      const invoiceData = {
        ...values,
        receiver_id: selectedClient?.id || null,
        lugar_expedicion: selectedCompany?.tax_location || '',
        serie: selectedSerie?.serie || values.serie,
        conceptos: normalizedItems,
        subtotal,
        taxes: taxes || [],
        total,
        fecha: values.fecha || getMexicoCityDateTimeISO()
      };

      let invoiceId = loadedInvoice?.id;
      if (invoiceId) {
        await userService.updateInvoice(invoiceId, invoiceData);
      } else {
        await userService.createInvoice(invoiceData);
      }
      
      message.success('Borrador guardado exitosamente');
      navigate('/invoices');
    } catch (error) {
      console.error('Error saving draft:', error);
      const msg = error.response?.data?.message || 'Error al guardar el borrador';
      message.error(msg);
    } finally {
      setDraftLoading(false);
    }
  };

  const downloadBase64Pdf = (base64Content, filename) => {
    if (!base64Content) {
      message.error('El PDF no se recibió correctamente.');
      return;
    }
    const cleaned = base64Content.includes(',')
      ? base64Content.split(',').pop()
      : base64Content;
    const byteChars = atob(cleaned);
    const byteNums = new Array(byteChars.length);
    for (let i = 0; i < byteChars.length; i++) {
      byteNums[i] = byteChars.charCodeAt(i);
    }
    const byteArray = new Uint8Array(byteNums);
    const blob = new Blob([byteArray], { type: 'application/pdf' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const handleDownloadPdf = async () => {
    if (!loadedInvoice?.id) return;
    try {
      const result = await userService.generateInvoicePdf(loadedInvoice.id, { templateId: 'complitcfdiv40' });
      const base64 = result?.data?.pdf_content;
      const docPrefix = loadedInvoice?.tipo_comprobante === 'N' ? 'Nomina_' : 'CFDI_';
      const filename = `${docPrefix}${loadedInvoice?.serie || ''}_${loadedInvoice?.folio || ''}_${loadedInvoice?.uuid || 'borrador'}.pdf`;
      downloadBase64Pdf(base64, filename);
    } catch (err) {
      const msg = err.response?.data?.message || 'No se pudo generar/descargar el PDF.';
      message.error(msg);
    }
  };

  const handlePreviewPdf = async () => {
    // Si no hay factura guardada, creamos una "al vuelo" para la vista previa
    let invoiceToPreviewId = loadedInvoice?.id;
    let tempInvoiceData = null;

    if (!invoiceToPreviewId) {
        try {
            // Validar campos mínimos antes de intentar previsualizar
            const values = await form.validateFields();
            if (invoiceItems.length === 0) {
                message.error('Debe agregar al menos un producto para la vista previa');
                return;
            }

            const selectedSerie = folioSeries.find(fs => fs.id === values.folio_serie_id);
            const normalizedItems = invoiceItems.map(item => {
                const product = item.product || null;
                const price = Number(item.price || product?.price || 0);
                const amount = (item.quantity || 0) * price;
                return {
                    ...item,
                    product_id: item.product_id || product?.id || null,
                    description: item.description || product?.description || '',
                    price,
                    amount,
                    clave_unidad: product?.claveunidad || '',
                    clave_prod_serv: product?.claveprod || '',
                    cantidad: item.quantity || 0,
                    descripcion: item.description || '',
                    valor_unitario: price,
                    importe: amount,
                    impuestos: item.taxes || []
                };
            });

            tempInvoiceData = {
                ...values,
                receiver_id: selectedClient?.id || null,
                lugar_expedicion: selectedCompany?.tax_location || '',
                serie: selectedSerie?.serie || values.serie,
                conceptos: normalizedItems,
                subtotal,
                taxes: taxes || [],
                total,
                fecha: values.fecha || getMexicoCityDateTimeISO(),
                // Flag para indicar que es una previsualización temporal
                is_preview: true 
            };
        } catch (e) {
            console.error('Validation failed for preview:', e);
            message.error('Complete los campos requeridos para la vista previa');
            return;
        }
    }

    try {
      setPreviewLoading(true);
      // Si tenemos ID usamos el endpoint existente, si no, enviamos los datos crudos (raw preview)
      let result;
      if (invoiceToPreviewId) {
          result = await userService.previewInvoicePdf(invoiceToPreviewId, { templateId: 'complitcfdiv40' });
      } else {
          // Preview raw sin guardar en BD
          result = await userService.previewInvoicePdf(tempInvoiceData, { templateId: 'complitcfdiv40' });
      }

      const base64 = result?.data?.pdf?.pdf_content;
      
      if (!base64) {
          throw new Error('No se recibió el PDF');
      }

      const cleaned = base64.includes(',') ? base64.split(',').pop() : base64;
      const byteChars = atob(cleaned);
      const byteNums = new Array(byteChars.length);
      for (let i = 0; i < byteChars.length; i++) {
        byteNums[i] = byteChars.charCodeAt(i);
      }
      const byteArray = new Uint8Array(byteNums);
      const blob = new Blob([byteArray], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      
      setPdfPreviewUrl(url);
      setPreviewVisible(true);
    } catch (err) {
      console.error(err);
      const msg = err.response?.data?.message || 'No se pudo generar la vista previa.';
      message.error(msg);
    } finally {
      setPreviewLoading(false);
    }
  };

  const columns = [
    {
      title: 'Producto',
      dataIndex: 'product',
      key: 'product',
      width: 200,
      render: (_, record) => (
        <Select
          placeholder="Buscar producto"
          showSearch
          style={{ width: '100%' }}
          value={record.product_id || undefined}
          onSearch={(value) => handleProductSearch(record.key, value)}
          onChange={(value) => handleProductSelect(record.key, value)}
          onFocus={() => fetchProducts('')}
          filterOption={false}
          suffixIcon={<SearchOutlined />}
        >
          {products.map(product => (
            <Option key={product.id} value={product.id}>
              {product.name} 
              {product.claveprod ? ` (${product.claveprod})` : ''}
            </Option>
          ))}
          {/* Mostrar producto cargado si no está en la lista de búsqueda */}
          {record.product && !products.find(p => p.id === record.product.id) && (
            <Option key={record.product.id} value={record.product.id}>
              {record.product.name}
              {record.product.claveprod ? ` (${record.product.claveprod})` : ''}
            </Option>
          )}
        </Select>
      ),
    },
    {
      title: 'Descripción',
      dataIndex: 'description',
      key: 'description',
      width: 200,
      render: (text, record) => (
        <Input.TextArea
          autoSize={{ minRows: 1, maxRows: 3 }}
          value={text}
          onChange={(e) => updateItem(record.key, 'description', e.target.value)}
          placeholder="Descripción"
        />
      ),
    },
    {
      title: 'Cantidad',
      dataIndex: 'quantity',
      key: 'quantity',
      width: 90,
      render: (text, record) => (
        <InputNumber
          min={0.000001}
          value={text}
          onChange={(value) => updateItem(record.key, 'quantity', value || 1)}
          style={{ width: '100%' }}
        />
      ),
    },
    {
      title: 'Precio',
      dataIndex: 'price',
      key: 'price',
      width: 180,
      render: (text, record) => (
        <InputNumber
          min={0}
          step={0.000001}
          value={text}
          onChange={(value) => updateItem(record.key, 'price', value || 0)}
          prefix="$"
          style={{ width: '100%' }}
          stringMode
          precision={6}
          formatter={(value) => {
            if (!value) return '';
            const valStr = String(value);
            const parts = valStr.split('.');
            // Force 6 decimal places truncation
            if (parts.length > 1 && parts[1].length > 6) {
              return `${parts[0]}.${parts[1].substring(0, 6)}`;
            }
            return valStr;
          }}
          parser={(value) => {
            // Remove non-numeric characters except dot
            const clean = value.replace(/[^\d.]/g, '');
            const parts = clean.split('.');
            // Force 6 decimal places truncation
            if (parts.length > 1 && parts[1].length > 6) {
              return `${parts[0]}.${parts[1].substring(0, 6)}`;
            }
            return clean;
          }}
        />
      ),
    },
    {
      title: 'Impuestos',
      dataIndex: 'selectedTaxIds',
      key: 'taxes',
      width: 180,
      render: (selectedIds, record) => (
        <Select
            mode="multiple"
            style={{ width: '100%' }}
            placeholder="Impuestos"
            value={selectedIds}
            onChange={(values) => updateItem(record.key, 'selectedTaxIds', values)}
            maxTagCount="responsive"
            allowClear
        >
            {activeTaxes.map(tax => (
                <Option key={tax.id} value={tax.id}>
                    {tax.tax_code} {tax.tax_type === 'Traslado' ? 'T' : 'R'} {(Number(tax.tasa_o_cuota)*100).toFixed(0)}%
                </Option>
            ))}
        </Select>
      ),
    },
    {
      title: 'Importe',
      dataIndex: 'amount',
      key: 'amount',
      width: 100,
      render: (text, record) => (
        <Text strong>
          ${(record.quantity * record.price).toFixed(2)}
        </Text>
      ),
    },
    {
      title: 'Acciones',
      key: 'actions',
      width: 60,
      render: (_, record) => (
        <Button
          type="text"
          danger
          icon={<DeleteOutlined />}
          onClick={() => removeItem(record.key)}
        />
      ),
    },
  ];

  return (
    <div style={{ height: '100%', overflow: 'auto' }}>
      <div style={{ marginBottom: '24px' }}>
        <Button
          icon={<ArrowLeftOutlined />}
          onClick={() => navigate('/invoices')}
          style={{ marginBottom: '16px' }}
        >
          Volver a Facturas
        </Button>
        <Row align="middle" justify="space-between" gutter={[16, 16]}>
          <Col xs={24} md={12} style={{ textAlign: screens.md ? 'left' : 'center' }}>
            <Title level={2} style={{ margin: 0 }}>Nueva Factura</Title>
          </Col>
          <Col xs={24} md={12} style={{ display: 'flex', justifyContent: screens.md ? 'flex-end' : 'center' }}>
            <div style={{ width: '100%', maxWidth: '350px' }}>
              <Select
                style={{ width: '100%' }}
                placeholder="Template de Impuestos (Opcional)"
                disabled={!selectedCompany || !taxTemplates || taxTemplates.length === 0}
                onChange={handleTaxTemplateChange}
                allowClear
              >
                <Option value="none">Sin Plantilla (Usar impuestos activos)</Option>
                {taxTemplates && taxTemplates.map(template => (
                  <Option key={template.id} value={template.id}>
                    {template.name}
                  </Option>
                ))}
              </Select>
            </div>
          </Col>
        </Row>
      </div>

      <Form
        form={form}
        layout="vertical"
        onFinish={handleSubmit}
        initialValues={{
          fecha: getMexicoCityDateTimeISO(initialDate),
          regimen_fiscal: 'Régimen General de Ley Personas Morales',
          forma_pago: '01', // Efectivo por defecto
          metodo_pago: 'PUE',
        tipo_comprobante: 'I',
        uso_cfdi: 'G03', // Valor por defecto
        moneda: 'MXN'
        }}
      >
        <Row gutter={24}>
          <Col xs={24} lg={18}>
            <CompanyInfoSection
              companies={companies}
              certificates={certificates}
              folioSeries={folioSeries}
              selectedCompany={selectedCompany}
              existingInvoiceDate={existingInvoiceDate}
              getDateOptions={getDateOptions}
              handleCompanySelect={handleCompanySelect}
              handleCertificateSelect={handleCertificateSelect}
              certificateAlert={certificateAlert}
              disableSeries={Boolean(loadedInvoice)}
              loadedSerie={loadedInvoice?.serie}
              loadedFolio={loadedInvoice?.folio}
            />

            <ClientInfoSection 
        clients={clients} 
        usoCfdiOptions={usoCfdiOptions}
        handleClientSelect={handleClientSelect}
        onSearch={handleClientSearch}
      />

            <ItemsTableSection
              columns={columns}
              invoiceItems={invoiceItems}
              addItem={addItem}
            />
          </Col>

          <Col xs={24} lg={6}>
            <ConditionsSection
              formaPagoOptions={formaPagoOptions}
              metodoPagoOptions={metodoPagoOptions}
              tipoComprobanteOptions={tipoComprobanteOptions}
            />
            
            <ActionsSection
              loadedInvoice={loadedInvoice}
              loading={loading}
              draftLoading={draftLoading}
              previewLoading={previewLoading}
              handleSaveDraft={handleSaveDraft}
              handleDownloadPdf={handleDownloadPdf}
              handlePreviewPdf={handlePreviewPdf}
              subtotal={subtotal}
              taxes={taxes}
              total={total}
              disableStamp={certificateAlert?.type === 'error'}
            />
          </Col>
        </Row>
      </Form>

      <Modal
        title="Vista previa del PDF"
        open={previewVisible}
        onCancel={() => {
          setPreviewVisible(false);
          if (pdfPreviewUrl) {
            window.URL.revokeObjectURL(pdfPreviewUrl);
            setPdfPreviewUrl(null);
          }
        }}
        footer={[
          <Button key="close" onClick={() => {
            setPreviewVisible(false);
            if (pdfPreviewUrl) {
              window.URL.revokeObjectURL(pdfPreviewUrl);
              setPdfPreviewUrl(null);
            }
          }}>
            Cerrar
          </Button>
        ]}
        width={1000}
        style={{ top: 20 }}
        bodyStyle={{ height: '80vh', padding: 0 }}
      >
        {pdfPreviewUrl && (
          <iframe
            src={pdfPreviewUrl}
            style={{ width: '100%', height: '100%', border: 'none' }}
            title="PDF Preview"
          />
        )}
      </Modal>
    </div>
  );
};

export default CreateInvoice;
